/**
 * Optimized Site Map Data Fetcher
 * Fetches polygons with their units in a single query and caches the result
 */
import { supabase } from '@/integrations/supabase/client';
import type { PropertyUnit } from '@/lib/units';
import type { MapCoordinates, UnitFilters } from '@/types/map';

export interface SiteMapPolygon {
  id: string;
  block: 'A' | 'B';
  polygon_number: number;
  map_coordinates: MapCoordinates;
  created_at: string;
  updated_at: string;
}

export interface SiteMapData {
  polygons: SiteMapPolygon[];
  units: PropertyUnit[];
  polygonUnitsMap: Map<string, PropertyUnit[]>;
}

// Cache configuration
let cachedData: SiteMapData | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Clear the cache (useful for admin operations that modify data)
 */
export function clearSiteMapCache(): void {
  cachedData = null;
  cacheTimestamp = 0;
}

/**
 * Check if cache is valid
 */
function isCacheValid(): boolean {
  return cachedData !== null && Date.now() - cacheTimestamp < CACHE_TTL;
}

/**
 * Match units to polygons using fallback logic when site_map_polygon_id is not set
 */
function matchUnitsToPolygon(
  polygon: SiteMapPolygon,
  allUnits: PropertyUnit[]
): PropertyUnit[] {
  const polygonNumber = polygon.polygon_number;
  
  if (polygon.block === 'A') {
    // Block A: numeric-only (1, 2, 10), letter suffix (11A, 11B, 11C), letter prefix (A11, B11, C11)
    return allUnits.filter((unit) => {
      if (unit.block !== 'A') return false;
      
      // Try numeric-only format: "1", "2", "10"
      if (/^\d+$/.test(unit.unit_number)) {
        return parseInt(unit.unit_number, 10) === polygonNumber;
      }
      
      // Try letter suffix format: "11A", "11B", "11C" (new format)
      const letterSuffixMatch = unit.unit_number.match(/^(\d+)([A-Z])$/i);
      if (letterSuffixMatch) {
        return parseInt(letterSuffixMatch[1], 10) === polygonNumber;
      }
      
      // Try letter prefix format: "A11", "B11", "C11" (legacy format)
      const letterPrefixMatch = unit.unit_number.match(/^([A-Z])(\d+)$/i);
      if (letterPrefixMatch) {
        return parseInt(letterPrefixMatch[2], 10) === polygonNumber;
      }
      
      // Try old format: "A-01", "A-02"
      const oldFormatMatch = unit.unit_number.match(/A-(\d+)/);
      if (oldFormatMatch) {
        return parseInt(oldFormatMatch[1], 10) === polygonNumber;
      }
      
      return false;
    });
  } else if (polygon.block === 'B') {
    // Block B: "12A", "12B", "12C" format or "B-12"
    return allUnits.filter((unit) => {
      if (unit.block !== 'B') return false;
      
      // Try new format: "12A", "12B", "12C"
      const unitNumMatch = unit.unit_number.match(/^(\d+)([A-Z])?$/i);
      if (unitNumMatch) {
        return parseInt(unitNumMatch[1], 10) === polygonNumber;
      }
      
      // Try old format: "B-12"
      const oldFormatMatch = unit.unit_number.match(/B-(\d+)/);
      if (oldFormatMatch) {
        return parseInt(oldFormatMatch[1], 10) === polygonNumber;
      }
      
      return false;
    });
  }
  
  return [];
}

/**
 * Fetch all site map data in a single optimized query
 * Returns polygons, units, and a pre-built map of polygon -> units
 */
export async function getSiteMapData(
  forceRefresh = false
): Promise<{ data: SiteMapData | null; error: string | null }> {
  // Return cached data if valid
  if (!forceRefresh && isCacheValid()) {
    return { data: cachedData, error: null };
  }

  try {
    // Single query: fetch polygons with their embedded units
    const { data: polygonsWithUnits, error: polygonsError } = await supabase
      .from('site_map_polygons')
      .select(`
        *,
        property_units (*)
      `)
      .order('block')
      .order('polygon_number');

    if (polygonsError) {
      console.error('Error fetching site map data:', polygonsError);
      return { data: null, error: polygonsError.message };
    }

    if (!polygonsWithUnits) {
      return { data: null, error: 'No data returned' };
    }

    // Also fetch all published units for fallback matching
    const { data: allUnits, error: unitsError } = await supabase
      .from('property_units')
      .select('*')
      .eq('is_published', true);

    if (unitsError) {
      console.error('Error fetching units:', unitsError);
      return { data: null, error: unitsError.message };
    }

    const units = (allUnits || []) as PropertyUnit[];
    const polygons: SiteMapPolygon[] = [];
    const polygonUnitsMap = new Map<string, PropertyUnit[]>();
    const assignedUnitIds = new Set<string>();

    // Process each polygon
    for (const polygonData of polygonsWithUnits) {
      const { property_units, ...polygon } = polygonData;
      polygons.push(polygon as SiteMapPolygon);

      // Get units directly assigned to this polygon via site_map_polygon_id
      const directUnits = (property_units || []) as PropertyUnit[];
      
      if (directUnits.length > 0) {
        // Filter to only published units
        const publishedUnits = directUnits.filter(u => u.is_published);
        if (publishedUnits.length > 0) {
          polygonUnitsMap.set(polygon.id, publishedUnits);
          publishedUnits.forEach(u => assignedUnitIds.add(u.id));
        }
      }
    }

    // Second pass: fallback matching for polygons without direct assignments
    for (const polygon of polygons) {
      if (!polygonUnitsMap.has(polygon.id)) {
        // Try to match units by number pattern
        const matchedUnits = matchUnitsToPolygon(polygon, units)
          .filter(u => !assignedUnitIds.has(u.id));
        
        if (matchedUnits.length > 0) {
          polygonUnitsMap.set(polygon.id, matchedUnits);
          matchedUnits.forEach(u => assignedUnitIds.add(u.id));
        }
      }
    }

    // Build result
    const result: SiteMapData = {
      polygons,
      units,
      polygonUnitsMap,
    };

    // Update cache
    cachedData = result;
    cacheTimestamp = Date.now();

    return { data: result, error: null };
  } catch (error) {
    console.error('Error in getSiteMapData:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

/**
 * Get filtered site map data
 * Applies filters to the cached/fetched data
 */
export async function getFilteredSiteMapData(
  filters?: UnitFilters,
  forceRefresh = false
): Promise<{ data: SiteMapData | null; error: string | null }> {
  const { data, error } = await getSiteMapData(forceRefresh);
  
  if (error || !data) {
    return { data: null, error };
  }

  // If no filters, return all data
  if (!filters || Object.keys(filters).length === 0) {
    return { data, error: null };
  }

  // Filter units
  const filteredUnits = data.units.filter(unit => {
    if (filters.viewType && filters.viewType !== 'all' && unit.view_type !== filters.viewType) {
      return false;
    }
    if (filters.availability && filters.availability !== 'all' && unit.availability !== filters.availability) {
      return false;
    }
    if (filters.category && filters.category !== 'all' && unit.category !== filters.category) {
      return false;
    }
    if (filters.floor && filters.floor !== 'all') {
      // Handle special case for Penthouse (fourth floor)
      if (filters.floor === 'fourth') {
        const isPenthouse = 
          unit.floor === 'fourth' || 
          (unit.floor === 'third' && 
           unit.floors_included && 
           unit.floors_included.includes('fourth') && 
           unit.category !== 'townhouse');
        if (!isPenthouse) return false;
      } else {
        // Inclusive: check if unit includes this floor (primary OR floors_included)
        const unitIncludesFloor = 
          unit.floor === filters.floor ||
          (unit.floors_included?.includes(filters.floor));
        if (!unitIncludesFloor) return false;
      }
    }
    if (filters.block && unit.block !== filters.block) {
      return false;
    }
    return true;
  });

  // Rebuild polygon units map with filtered units
  const filteredPolygonUnitsMap = new Map<string, PropertyUnit[]>();
  const filteredUnitIds = new Set(filteredUnits.map(u => u.id));

  for (const [polygonId, units] of data.polygonUnitsMap) {
    const filtered = units.filter(u => filteredUnitIds.has(u.id));
    if (filtered.length > 0) {
      filteredPolygonUnitsMap.set(polygonId, filtered);
    }
  }

  return {
    data: {
      polygons: data.polygons,
      units: filteredUnits,
      polygonUnitsMap: filteredPolygonUnitsMap,
    },
    error: null,
  };
}
